import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const CURRENCY_KEY_PREFIX = "preferred_currency_";

export type CurrencyOverride = "GBP" | "USD" | "EUR" | "CHF" | "PLN";

export interface CurrencyConfig {
  code: CurrencyOverride;
  symbol: string;
  label: string;
}

// Per-locale selectable currencies. Default (i18n currency.code) is always EUR now.
export const LOCALE_CURRENCIES: Record<string, CurrencyConfig[]> = {
  en: [
    { code: "EUR", symbol: "€", label: "EUR (€)" },
    { code: "GBP", symbol: "£", label: "GBP (£)" },
    { code: "USD", symbol: "$", label: "USD ($)" },
  ],
  de: [
    { code: "EUR", symbol: "€", label: "EUR (€)" },
    { code: "CHF", symbol: "CHF", label: "CHF (Fr.)" },
  ],
  pl: [
    { code: "PLN", symbol: "zł", label: "PLN (zł)" },
    { code: "EUR", symbol: "€", label: "EUR (€)" },
  ],
  nl: [
    { code: "EUR", symbol: "€", label: "EUR (€)" },
    { code: "USD", symbol: "$", label: "USD ($)" },
  ],
};

export const getLocaleKey = (lang: string): string => {
  const base = (lang || "en").split("-")[0].toLowerCase();
  return LOCALE_CURRENCIES[base] ? base : "";
};


export const getCurrencyOverride = (lang?: string): CurrencyOverride | null => {
  if (typeof window === "undefined") return null;
  const localeKey = getLocaleKey(lang || "");
  if (!localeKey) return null;
  const stored = localStorage.getItem(CURRENCY_KEY_PREFIX + localeKey);
  const allowed = LOCALE_CURRENCIES[localeKey].map((c) => c.code);
  if (stored && (allowed as string[]).includes(stored)) return stored as CurrencyOverride;
  return null;
};

export const setCurrencyOverride = (lang: string, currency: CurrencyOverride) => {
  const localeKey = getLocaleKey(lang);
  if (!localeKey) return;
  localStorage.setItem(CURRENCY_KEY_PREFIX + localeKey, currency);
};

export const CurrencySelector = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const localeKey = getLocaleKey(i18n.language);
  const currencies = localeKey ? LOCALE_CURRENCIES[localeKey] : [];
  const [selected, setSelected] = useState<CurrencyOverride>(
    getCurrencyOverride(i18n.language) || (currencies[0]?.code ?? "EUR")
  );

  useEffect(() => {
    const next = getCurrencyOverride(i18n.language) || (currencies[0]?.code ?? "EUR");
    setSelected(next);
  }, [i18n.language, currencies]);

  if (!localeKey || currencies.length < 2) return null;

  const handleSelect = (code: CurrencyOverride) => {
    setSelected(code);
    setCurrencyOverride(i18n.language, code);
    setIsOpen(false);
    window.dispatchEvent(new Event("currency-change"));
  };

  const current = currencies.find((c) => c.code === selected) || currencies[0];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-primary hover:text-primary/80 font-bold text-sm px-2"
          aria-label="Select currency"
        >
          {current.symbol}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2" align="end">
        <div className="flex flex-col gap-1">
          {currencies.map((c) => (
            <button
              key={c.code}
              onClick={() => handleSelect(c.code)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all hover:bg-primary/10 text-sm font-medium ${
                selected === c.code
                  ? "ring-2 ring-primary bg-primary/5 text-primary"
                  : "text-foreground"
              }`}
            >
              <span className="font-bold text-base">{c.symbol}</span>
              <span>{c.label}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
